export function initWorkSlider() {
  const slider = document.querySelector(".work__slider");
  if (!slider) return;

  const track = slider.querySelector(".work__slides-track");
  const progressBar = slider.querySelector(".work__progress-bar");
  const prevBtn = slider.querySelector(".work__arrow--prev");
  const nextBtn = slider.querySelector(".work__arrow--next");

  if (!track || !progressBar) return;

  // IMPORTANT:
  // We query slides from the slider (not the track) so we get the original "real" slides
  // before we clear/rebuild the track with clones.
  const slideEls = Array.from(slider.querySelectorAll(".work__slide"));
  const slideCount = slideEls.length;

  // If no slides, nothing to do.
  if (slideCount === 0) return;

  // ---- Build / refresh dots dynamically (fixes "only 2 projects" issue) ----
  const dotsWrap = slider.querySelector(".work__dots");
  let dots = [];

  function rebuildDots() {
    if (!dotsWrap) {
      dots = [];
      return;
    }

    // Clear any hardcoded dots (or previous builds)
    dotsWrap.innerHTML = "";

    // For a single slide, dots are not useful
    if (slideCount <= 1) {
      dots = [];
      dotsWrap.setAttribute("aria-hidden", "true");
      return;
    }

    dotsWrap.removeAttribute("aria-hidden");

    dots = Array.from({ length: slideCount }, (_, i) => {
      const btn = document.createElement("button");
      btn.type = "button";
      btn.className = "dot";
      btn.setAttribute("aria-label", `Go to project ${i + 1}`);
      btn.dataset.index = String(i);
      dotsWrap.appendChild(btn);
      return btn;
    });
  }

  rebuildDots();

  // Disable arrows when there is only one slide
  const isSingle = slideCount <= 1;
  if (prevBtn) prevBtn.disabled = isSingle;
  if (nextBtn) nextBtn.disabled = isSingle;

  // ---- Build clones (infinite loop) ----
  // If single slide, we still render it, but infinite loop behavior is unnecessary.
  // We'll keep the clone logic safe, but navigation will be disabled anyway.
  const firstClone = slideEls[0].cloneNode(true);
  firstClone.classList.add("is-clone");
  const lastClone = slideEls[slideCount - 1].cloneNode(true);
  lastClone.classList.add("is-clone");

  track.innerHTML = "";
  track.appendChild(lastClone);
  slideEls.forEach((s) => track.appendChild(s));
  track.appendChild(firstClone);

  // Track index includes clones: [lastClone, real0..realN-1, firstClone]
  let currentIndex = 1; // start at first real
  let currentSlide = 0; // UI index 0..N-1

  const slideDuration = 7000;
  const resumeDelayMs = 1500;

  let inView = false;
  let running = document.visibilityState === "visible";

  // Autoplay state
  let isPaused = true; // we start paused; autoplay begins after idle in-view
  let isAnimating = false;

  // timers
  let autoplayTimer = null;
  let resumeTimer = null;

  // progress timing
  let progressStart = 0;
  let elapsedBeforePause = 0;

  // touch/swipe
  let touchStartX = 0;
  let touchEndX = 0;
  let isDragging = false;

  // ---------- Slide state (scroll positions + hint dismissal) ----------
  const slideState = new Map(); // key: realIndex => { hintDismissed, scrollTops: number[] }

  function getRealSlideByIndex(realIndex) {
    return track.children[realIndex + 1] || null;
  }

  function captureSlideState(realIndex) {
    const slide = getRealSlideByIndex(realIndex);
    if (!slide) return;

    const devices = slide.querySelector(".work__devices");
    const viewports = slide.querySelectorAll(".work__device-viewport");

    slideState.set(realIndex, {
      hintDismissed: !!devices?.classList.contains("is-scrolled"),
      scrollTops: Array.from(viewports).map((vp) => vp.scrollTop),
    });
  }

  function applySlideState(realIndex) {
    const slide = getRealSlideByIndex(realIndex);
    if (!slide) return;

    const state = slideState.get(realIndex);
    if (!state) return;

    const devices = slide.querySelector(".work__devices");
    const viewports = slide.querySelectorAll(".work__device-viewport");

    if (devices) devices.classList.toggle("is-scrolled", !!state.hintDismissed);

    Array.from(viewports).forEach((vp, i) => {
      const t = state.scrollTops?.[i];
      if (typeof t === "number") vp.scrollTop = t;
    });
  }

  function setActiveSlideClass() {
    for (let i = 0; i < slideCount; i++) {
      getRealSlideByIndex(i)?.classList.toggle("is-active", i === currentSlide);
    }
  }

  function syncCurrentSlideFromIndex() {
    currentSlide = (currentIndex - 1 + slideCount) % slideCount;
  }

  function updateDots() {
    if (!dots.length) return;
    dots.forEach((dot, i) => dot.classList.toggle("active", i === currentSlide));
  }

  function setTrackTransition(enabled) {
    track.style.transition = enabled ? "transform 0.6s ease" : "none";
  }

  function updateSlider(instant = false) {
    if (currentIndex < 0) currentIndex = 0;
    if (currentIndex > slideCount + 1) currentIndex = slideCount + 1;

    if (!instant) isAnimating = true;
    if (instant) setTrackTransition(false);

    const offset = -currentIndex * 100;
    track.style.transform = `translate3d(${offset}%, 0, 0)`;

    if (instant) {
      track.offsetHeight; // reflow
      setTrackTransition(true);
    }

    updateDots();
    setActiveSlideClass();

    if (instant) applySlideState(currentSlide);
  }

  function snapIfOnClone() {
    // If single slide, snapping isn't meaningful (but currentIndex bounds still okay)
    if (slideCount <= 1) return;

    if (currentIndex === 0) {
      currentIndex = slideCount;
      syncCurrentSlideFromIndex();
      updateSlider(true);
    } else if (currentIndex === slideCount + 1) {
      currentIndex = 1;
      syncCurrentSlideFromIndex();
      updateSlider(true);
    }
  }

  track.addEventListener("transitionend", (e) => {
    if (e.propertyName !== "transform") return;
    isAnimating = false;

    snapIfOnClone();
    applySlideState(currentSlide);
    setActiveSlideClass();
  });

  // ---------- Progress ----------
  function setProgress(percent, instant = false) {
    if (instant) {
      progressBar.style.transition = "none";
      progressBar.style.width = `${percent}%`;
      progressBar.offsetHeight;
      return;
    }
    progressBar.style.width = `${percent}%`;
  }

  function clearAutoplayTimer() {
    if (autoplayTimer) {
      clearTimeout(autoplayTimer);
      autoplayTimer = null;
    }
  }

  function clearResumeTimer() {
    if (resumeTimer) {
      clearTimeout(resumeTimer);
      resumeTimer = null;
    }
  }

  function resetSlideTiming() {
    elapsedBeforePause = 0;
    setProgress(0, true);
  }

  function resumeFromPause() {
    if (!running || !inView) return;
    const remaining = Math.max(0, slideDuration - elapsedBeforePause);
    startAutoplayNow(remaining);
  }

  function shouldRunAutoplay() {
    // Don’t autoplay if there’s nothing to advance.
    if (slideCount <= 1) return false;
    return running && inView && !isPaused;
  }

  function startProgress(remainingMs) {
    progressBar.style.transition = `width ${remainingMs}ms linear`;
    const pct = Math.min((elapsedBeforePause / slideDuration) * 100, 100);
    setProgress(pct, true);
    progressBar.style.transition = `width ${remainingMs}ms linear`;
    requestAnimationFrame(() => setProgress(100, false));
  }

  function scheduleNextSlide(remainingMs) {
    clearAutoplayTimer();
    autoplayTimer = setTimeout(() => {
      if (!shouldRunAutoplay()) return;
      nextSlide({ userInitiated: false });
    }, remainingMs);
  }

  function startAutoplayNow(remainingMs = slideDuration) {
    clearResumeTimer();
    clearAutoplayTimer();

    isPaused = false;

    const remaining = Math.max(0, remainingMs);

    progressStart = performance.now();
    startProgress(remaining);
    scheduleNextSlide(remaining);
  }

  // The key: only start autoplay after idle time (and only if allowed)
  function scheduleIdleStart() {
    clearResumeTimer();
    if (!running || !inView) return;
    if (!isPaused) return;

    // If only one slide, don’t start timers at all.
    if (slideCount <= 1) return;

    resumeTimer = setTimeout(() => {
      if (!running || !inView) return;
      if (!isPaused) return;
      resumeFromPause();
    }, resumeDelayMs);
  }

  // Any interaction should cancel idle start + pause immediately
  function haltAutoplay() {
    clearResumeTimer();
    clearAutoplayTimer();

    const computed = getComputedStyle(progressBar).width;
    progressBar.style.transition = "none";
    progressBar.style.width = computed;

    if (!isPaused) {
      const now = performance.now();
      const elapsed = now - progressStart + elapsedBeforePause;
      elapsedBeforePause = Math.min(elapsed, slideDuration);
      isPaused = true;
    } else {
      isPaused = true;
    }
  }

  // ---------- Slide actions ----------
  function goToSlide(slideUiIndex) {
    // Single slide: no-op
    if (slideCount <= 1) return;

    captureSlideState(currentSlide);

    currentIndex = slideUiIndex + 1;
    syncCurrentSlideFromIndex();
    updateSlider(false);

    elapsedBeforePause = 0;
    setProgress(0, true);

    haltAutoplay();
    scheduleIdleStart();
  }

  function nextSlide({ userInitiated } = { userInitiated: true }) {
    if (slideCount <= 1) return;
    if (isAnimating) return;

    if (userInitiated) {
      captureSlideState(currentSlide);
      haltAutoplay();
    }

    currentIndex += 1;
    syncCurrentSlideFromIndex();
    updateSlider(false);

    if (userInitiated) {
      resetSlideTiming();
      scheduleIdleStart();
    } else {
      resetSlideTiming();
      startAutoplayNow(slideDuration);
    }
  }

  function prevSlide() {
    if (slideCount <= 1) return;
    if (isAnimating) return;

    captureSlideState(currentSlide);
    haltAutoplay();

    currentIndex -= 1;
    syncCurrentSlideFromIndex();
    updateSlider(false);

    resetSlideTiming();
    scheduleIdleStart();
  }

  // ---------- Visibility / in-view ----------
  document.addEventListener("visibilitychange", () => {
    running = document.visibilityState === "visible";
    if (!running) {
      haltAutoplay();
    } else {
      scheduleIdleStart();
    }
  });

  const io = new IntersectionObserver(
    ([entry]) => {
      inView = entry.isIntersecting;

      if (!inView) {
        haltAutoplay();
      } else if (running) {
        scheduleIdleStart();
      }
    },
    { threshold: 0.25 },
  );
  io.observe(slider);

  // ---------- Hover / touch ----------
  slider.addEventListener("mouseenter", () => {
    haltAutoplay();
  });

  slider.addEventListener("mouseleave", () => {
    scheduleIdleStart();
  });

  slider.addEventListener(
    "touchstart",
    (e) => {
      touchStartX = e.touches[0].clientX;
      touchEndX = touchStartX;
      isDragging = true;
      haltAutoplay();
    },
    { passive: true },
  );

  slider.addEventListener(
    "touchmove",
    (e) => {
      if (!isDragging) return;
      touchEndX = e.touches[0].clientX;
    },
    { passive: true },
  );

  slider.addEventListener("touchend", () => {
    if (!isDragging) return;
    isDragging = false;

    const swipeThreshold = 50;
    const swipeDistance = touchStartX - touchEndX;

    if (Math.abs(swipeDistance) > swipeThreshold) {
      if (swipeDistance > 0) nextSlide({ userInitiated: true });
      else prevSlide();
    }

    scheduleIdleStart();
  });

  // ---------- Remember scroll + dismiss hint on first scroll ----------
  track.addEventListener(
    "scroll",
    (e) => {
      const el = e.target;
      if (!(el instanceof Element)) return;
      if (!el.classList.contains("work__device-viewport")) return;

      el.closest(".work__devices")?.classList.add("is-scrolled");
      captureSlideState(currentSlide);

      haltAutoplay();
      scheduleIdleStart();
    },
    { passive: true, capture: true },
  );

  // ---------- Dots + arrows ----------
  // Delegate dot clicks (works even though dots are created dynamically)
  if (dotsWrap) {
    dotsWrap.addEventListener("click", (e) => {
      const btn = e.target.closest(".dot");
      if (!btn) return;
      const index = Number(btn.dataset.index);
      if (!Number.isFinite(index)) return;
      goToSlide(index);
    });
  }

  prevBtn?.addEventListener("click", () => prevSlide());
  nextBtn?.addEventListener("click", () => nextSlide({ userInitiated: true }));

  // Seed initial states (so clone snaps always have something to apply)
  for (let i = 0; i < slideCount; i++) captureSlideState(i);

  // Init (start on first real slide, but DO NOT autoplay until idle-in-view)
  setTrackTransition(true);
  syncCurrentSlideFromIndex();
  updateSlider(true);

  // If only one slide, keep progress at 0 (or you can hide it in CSS)
  setProgress(0, true);

  scheduleIdleStart();
}
